import React, { useEffect, useState } from "react";
import styles from "./SetPassword.module.css";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import AssetsPack from "../../../util/AssetsPack";
import { useTranslation } from "react-i18next";
import { JokerButton, ButtonType } from "material-ui-jokerpro";
import InputBox, {
  InputBoxRoundedCornors,
  InputBoxState,
} from "../../../components/InputBox";
import StylePack from "../../../util/StylePack.js";
import { useAppDispatch } from "../../../store/hooks";
import { resetPassword } from "../../../store/forget-password-slice";
import { showConsoleError } from "../../../util/ConsoleMessage.js";
import TwoColLayout from "../../Templates/TwoColLayout";
import MainTemplate from "../../Templates/MainTemplate";
import Paragraph from "../../../components/Paragraph";
import TitleText from "../../../components/TitleText";
import ImageComponent from "../../../components/ImageComponent";

const SetPassword = () => {
  const navigate = useNavigate();
  const [passwordState, setPasswordState] = useState<InputBoxState | undefined>(
    InputBoxState.default
  );
  const [confirmPasswordState, setConfirmPasswordState] = useState<
    InputBoxState | undefined
  >(InputBoxState.default);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [password, setPassword] = useState("");
  const [errorValue, setErrorValue] = useState<string | undefined>(undefined);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmErrorValue, setConfirmErrorValue] = useState<
    string | undefined
  >(undefined);

  const handleContinue = async () => {
    if (password === confirmPassword) {
      try {
        await dispatch(resetPassword(password));
        navigate("/successful_reset");
      } catch (error: any) {
        setConfirmErrorValue(error.message);
        showConsoleError(error.message);
      }
    }
  };

  const isValidPassword = (value: string): boolean => {
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*[\d!@#$%^&*()_+{}:"<>?;'[\],.\-.]).{6,16}|(?=.*\d)(?=.*[!@#$%^&*()_+{}:"<>?;'[\],.\-.]).{6,16}$/;
    if (!passwordRegex.test(value)) return false;
    return true;
  };

  const getInputState = (value: any) => {
    if (value.length === 0) {
      return InputBoxState.default;
    }

    if (isValidPassword(value)) {
      return InputBoxState.success;
    } else {
      return InputBoxState.error;
    }
  };

  useEffect(() => {
    const validatePasswords = () => {
      const passwordStateValue = getInputState(password);
      const newErrorValue =
        passwordStateValue === InputBoxState.error
          ? "Invalid password."
          : undefined;
      setErrorValue(newErrorValue);
      setPasswordState(passwordStateValue);

      const confirmPasswordStateValue = getInputState(confirmPassword);
      let newConfirmErrorValue: string | undefined = undefined;

      if (confirmPasswordStateValue === InputBoxState.success) {
        if (password === confirmPassword) {
          setConfirmPasswordState(InputBoxState.success);
        } else {
          newConfirmErrorValue = "Password does not match.";
          setConfirmPasswordState(InputBoxState.error);
        }
      } else {
        if (confirmPasswordStateValue === InputBoxState.error) {
          newConfirmErrorValue = "Password does not match.";
        }
        setConfirmPasswordState(confirmPasswordStateValue);
      }

      setConfirmErrorValue(newConfirmErrorValue);
    };

    validatePasswords();
  }, [password, confirmPassword]);

  const rightContent = (
    <div className={styles["info-section"]}>
      <div className={styles["icon"]}>
        <ImageComponent src={AssetsPack.icons.ICON_LOCK.default} />
      </div>
      <Paragraph>{t("set_password_page.info")}</Paragraph>
    </div>
  );

  const leftContent = (
    <div className={styles["form-section"]}>
      <TitleText>{t("set_password_page.title")}</TitleText>
      <Paragraph>{t("set_password_page.description")}</Paragraph>

      <InputBox
        state={passwordState}
        roundedCornors={InputBoxRoundedCornors.all}
        type="password"
        placeholder={t("set_password_page.input_password")}
        value={password}
        onChange={(e: any) => setPassword(e.target.value)}
        errorMessage={errorValue}
      />

      <div className={styles["input-box"]}>
        <InputBox
          state={confirmPasswordState}
          roundedCornors={InputBoxRoundedCornors.all}
          type="password"
          placeholder={t("set_password_page.input_confirm_password")}
          value={confirmPassword}
          onChange={(e: any) => setConfirmPassword(e.target.value)}
          errorMessage={confirmErrorValue}
        />
      </div>

      <div>
        <JokerButton
          text={t("set_password_page.continue")}
          onClick={handleContinue}
          disabled={
            !(
              confirmPasswordState == InputBoxState.success &&
              passwordState == InputBoxState.success
            )
          }
          type={ButtonType.CTA}
          icon={AssetsPack.icons.ICON_GO.default}
          animated
          equal
        />
      </div>
    </div>
  );

  const body = (
    <TwoColLayout rightContent={rightContent} leftContent={leftContent} />
  );

  return (
    <MainTemplate
      body={body}
      footer={<Footer />}
      backgroundStyle={StylePack.BACKGROUND_LOGIN_STYLE}
      hasOverlayFooter
    />
  );
};

export default SetPassword;
